import { useAuth0 } from '@auth0/auth0-react'

import React, { useEffect, useState } from 'react'
import { ClaimPageService } from 'services/ClaimPages/ClaimPageService'

import { UserDTO } from '../models/UserDTO.model'
import UserService from '../services/UserService/UserService'

interface ClaimFormModalProps {
  organizationType: 'hospital' | 'agency'
  organizationId: number
  organizationName: string
  onClose: () => void
  onSuccess: () => void
}

export const ClaimFormModal: React.FC<ClaimFormModalProps> = ({
  organizationType,
  organizationId,
  organizationName,
  onClose,
  onSuccess,
}) => {
  const { user, isAuthenticated } = useAuth0()
  const [userDetails, setUserDetails] = useState<UserDTO | null>(null)

  const [formData, setFormData] = useState({
    proofUrl: '',
    requesterEmail: '',
    requesterName: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Format user data and prefill form on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      const formatted = UserService.formatUser(user)
      setUserDetails(formatted)
      setFormData({
        proofUrl: '',
        requesterEmail: formatted.email || '',
        requesterName: formatted.name || '',
      })
    }
  }, [isAuthenticated, user])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.requesterName.trim()) {
      newErrors.requesterName = 'Your name is required'
    }

    if (!formData.requesterEmail.trim()) {
      newErrors.requesterEmail = 'Your organization email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.requesterEmail)) {
      newErrors.requesterEmail = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const claimService = new ClaimPageService()

      // Build proofDocumentation object if proofUrl is provided
      const proofDocumentation = formData.proofUrl
        ? {
            linkedinUrl: formData.proofUrl,
          }
        : undefined

      await claimService.submitClaimRequest({
        organizationName: organizationName,
        pageId: organizationId,
        pageType: organizationType,
        proofDocumentation,
        requesterEmail: formData.requesterEmail,
        requesterName: formData.requesterName,
      })

      onSuccess() // close modal, show toast, etc.
    } catch (error: unknown) {
      console.error('Error submitting claim:', error)
      let errorMessage = 'An error occurred. Please try again.'

      // Handle axios error responses
      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as { message?: string }
        errorMessage = errorData?.message || errorMessage
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative z-[10001] bg-white rounded-xl max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Claim This Page</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-gray-600 mb-6">
            By claiming this page, you&apos;ll be able to update your organization&apos;s information, respond to
            reviews, and manage your public profile on Scrubstr.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Organization Name (Read-only) */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Organization Name</label>
              <input
                type="text"
                value={organizationName}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">This field is pre-filled from your page</p>
            </div>

            {/* Requester Name (Prefilled) */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Full Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="requesterName"
                value={formData.requesterName}
                onChange={handleChange}
                placeholder="John Doe"
                disabled={isSubmitting}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-aqua-mint focus:border-transparent transition-all ${
                  errors.requesterName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {userDetails?.name && <p className="text-xs text-gray-500 mt-1">✓ Pre-filled from your profile</p>}
              {errors.requesterName && <p className="text-xs text-red-600 mt-1">{errors.requesterName}</p>}
            </div>

            {/* Organization Email (Prefilled) */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Organization Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                name="requesterEmail"
                value={formData.requesterEmail}
                onChange={handleChange}
                placeholder="john.doe@organization.com"
                disabled={isSubmitting}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-aqua-mint focus:border-transparent transition-all ${
                  errors.requesterEmail ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <p className="text-xs text-gray-500 mt-1">Please use your official organization email address</p>
              {userDetails?.email && <p className="text-xs text-gray-500 mt-1">✓ Pre-filled from your profile</p>}
              {errors.requesterEmail && <p className="text-xs text-red-600 mt-1">{errors.requesterEmail}</p>}
            </div>

            {/* Proof URL */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Proof of Employment (Optional)</label>
              <input
                type="url"
                name="proofUrl"
                value={formData.proofUrl}
                onChange={handleChange}
                placeholder="LinkedIn profile URL, employee directory link, etc."
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-mint focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">
                A link to your LinkedIn profile or other verification can help expedite the review process
              </p>
            </div>

            {/* Submit Error */}
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}

            {/* Footer Note */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Your claim will be reviewed by our team. You&apos;ll receive an email notification once your claim has
                been approved or if we need additional information.
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-aqua-mint text-white rounded-lg font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Claim Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
